import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query"
import {
    addSchoolExamQuestions,
    changeAddQuestionChapter,
    changeExamStatus,
    getAssignChaptersByAIRag,
    getExamChapters,
    getExamData,
    getExamQuestionData,
    getExamQuestionStatus,
    getExamsList,
    getGrades,
    getGradeSubjects,
    getStudentProfile,
    getStudentResult,
    getStudentResultChapters,
    getStudentResults,
    getStudents,
    schoolChangeAddQuestion,
    submitSchoolAddExam,
    editExamInfo,
    schoolAddGrade,
    schoolAddSubject,
    getSubjectChaptersApi,
    addSubjectChapterApi,
    listTeachersApi,
    listTeacherAssignmentsApi,
    assignTeacherSubjectApi,
    unassignTeacherSubjectApi,
    getSubChapters,
    addSubChapter,
    getSubjectsList,
    getNationalExams,
    getUserInfo,
    getSharedNotes,
    createSharedNote,
    updateSharedNote,
    assignSharedNoteToQuestions,
    deleteExamQuestion,
} from "../utils/remote"
import { searchGrades, schoolJoinGrade } from "../utils/remote"
import { IQuestion } from "../types/question"
import { queryClient } from "../App"
import { useAuth } from "../utils/Auth/AuthProvider"

export function useStudents(toBeValidated: string | null, grade_id: string) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['students', grade_id],
        queryFn: () => getStudents(toBeValidated, grade_id),
        enabled: isAllowed && !!grade_id && grade_id !== '0' && grade_id !== '-1'
    })
}

export function useStudentProfile(toBeValidated: string | null, stud_id: string | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['student', stud_id],
        queryFn: () => getStudentProfile(toBeValidated, stud_id),
        enabled: isAllowed && !!stud_id
    })
}

export function useStudentResults(toBeValidated: string | null, stud_id: string | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['results', stud_id],
        queryFn: () => getStudentResults(toBeValidated, stud_id),
        enabled: isAllowed && !!stud_id,
    })
}

export function useStudentResult(toBeValidated: string | null, stud_id: string | undefined, res_id: number | undefined, get_res: boolean) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['result', res_id],
        queryFn: () => getStudentResult(toBeValidated, stud_id, res_id),
        enabled: isAllowed && get_res && !!stud_id && !!res_id,
    })
}

export function useStudentResultChapters(toBeValidated: string | null, stud_id: string | undefined, res_id: number | undefined, get_res: boolean) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['chapters', res_id],
        queryFn: () => getStudentResultChapters(toBeValidated, stud_id, res_id),
        enabled: isAllowed && get_res && !!stud_id && !!res_id,
    })
}

export function useGrades(toBeValidated: string | null) {
    const { role } = useAuth();
    // grades endpoint is only valid for school admins and superusers (not for university/college flow)
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school');
    return useQuery({
        queryKey: ['grades'],
        queryFn: () => getGrades(toBeValidated),
        enabled: isAllowed,
    })
}

export function useGradeSubjects(toBeValidated: string | null, grade_id: string) {
    const { role } = useAuth();
    // grade subjects endpoint is school-specific; disable for university/college flow
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school');
    return useQuery({
        queryKey: ['subjects', grade_id],
        queryFn: () => getGradeSubjects(toBeValidated, grade_id),
        enabled: isAllowed && grade_id !== '0' && grade_id !== '-1'
    })
}


export const useSchoolAddGrade = () => {
    return useMutation({
        mutationFn: (data:{toBeValidated: string | null, data: FormData }) => submitSchoolAddExam(data)
    })
}

// School Setup hooks
export function useSchoolGrades(toBeValidated: string | null) {
    return useGrades(toBeValidated);
}

export function useSchoolGradeSubjects(toBeValidated: string | null, grade_id: string) {
    return useGradeSubjects(toBeValidated, grade_id);
}

export function useAddGrade() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; name: string }) => schoolAddGrade(p.toBeValidated, p.name),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['grades'] }),
    });
}

export function useSearchGrades() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; q: string }) => searchGrades(p.toBeValidated, p.q),
    });
}

export function useJoinGrade() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; gradeId: number }) => schoolJoinGrade(p.toBeValidated, p.gradeId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['grades'] }),
    });
}

export function useAddSubject() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; gradeId: number; name?: string; subjectId?: number }) => schoolAddSubject(p.toBeValidated, p.gradeId, p.name, p.subjectId),
        onSuccess: (_d, v) => queryClient.invalidateQueries({ queryKey: ['subjects', String(v.gradeId)] }),
    });
}

export function useSubjectsList(toBeValidated: string | null) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['all_subjects'],
        queryFn: () => getSubjectsList(toBeValidated),
        enabled: isAllowed,
    });
}

export function useSubjectChapters(toBeValidated: string | null, subjectId: number | null, gradeId?: number | null) {
    const { role } = useAuth();
    // Also inspect the user's institution type to avoid calling school-specific endpoints for university accounts
    const { data: userInfo } = useUserInfo();
    const instType = (userInfo?.institution_type || '');
    const instLower = String(instType).toLowerCase();
    const isUniversity = instLower.includes('university') || instLower.includes('college') || role === 'college_university';
    // allow superuser always; otherwise only allow when not a university account and role is 'school'
    const isAllowed = !!toBeValidated && (role === 'superuser' || (!isUniversity && role === 'school'));
    return useQuery({
        queryKey: ['subject_chapters', subjectId, gradeId],
        queryFn: () => getSubjectChaptersApi(toBeValidated, subjectId as number, gradeId ?? null),
        enabled: isAllowed && !!subjectId,
    });
}

export function useAddSubjectChapter() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; subjectId: number; name: string; school_grade_id: number }) => addSubjectChapterApi(p.toBeValidated, p.subjectId, p.name, p.school_grade_id),
        onSuccess: (_d, v) => queryClient.invalidateQueries({ queryKey: ['subject_chapters', v.subjectId] }),
    });
}

export function useSubChapters(toBeValidated: string | null, chapterId: number | null) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['subchapters', chapterId],
        queryFn: () => getSubChapters(toBeValidated, chapterId),
        enabled: isAllowed && !!chapterId,
    });
}

export function useAddSubChapter() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; chapterId: number; name: string }) => addSubChapter(p),
        onSuccess: (_d, v) => queryClient.invalidateQueries({ queryKey: ['subchapters', v.chapterId] }),
    });
}

export function useTeachersList(toBeValidated: string | null) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['teachers'],
        queryFn: () => listTeachersApi(toBeValidated),
        enabled: isAllowed,
    });
}

export function useTeacherAssignmentsList(toBeValidated: string | null, teacherId: number | null) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['teacher_assignments', teacherId],
        queryFn: () => listTeacherAssignmentsApi(toBeValidated, teacherId as number),
        enabled: isAllowed && !!teacherId,
    });
}

export function useAssignTeacherSubject() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; teacherId: number; subjectId: number }) => assignTeacherSubjectApi(p.toBeValidated, p.teacherId, p.subjectId),
        onSuccess: (_d, v) => queryClient.invalidateQueries({ queryKey: ['teacher_assignments', v.teacherId] }),
    });
}

export function useUnassignTeacherSubject() {
    return useMutation({
        mutationFn: (p: { toBeValidated: string | null; teacherId: number; subjectId: number }) => unassignTeacherSubjectApi(p.toBeValidated, p.teacherId, p.subjectId),
        onSuccess: (_d, v) => queryClient.invalidateQueries({ queryKey: ['teacher_assignments', v.teacherId] }),
    });
}

export function useExamDetail(toBeValidated: string | null, exam_id: string | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['exams', exam_id],
        queryFn: () => getExamData(toBeValidated, exam_id),
        enabled: isAllowed && !!exam_id,
    })
}


export const useSchoolExamQuestionAdd = () => {
    return useMutation({
        mutationFn: (data:{toBeValidated: string | null, exam_id: string, data: IQuestion[]}) => addSchoolExamQuestions(data)
    })
}


export function useExamQuestionGet(toBeValidated: string | null, exam_id: string | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['questions', exam_id],
    // getExamQuestionData now returns { questions: mapped, full: rawArray }
    queryFn: () => getExamQuestionData(toBeValidated, exam_id),
        staleTime: 120000,
        enabled: isAllowed && !!exam_id,
    })
}


export function useExamQuestionStatusGet(toBeValidated: string | null, exam_id: string | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['status', exam_id],
        queryFn: () => getExamQuestionStatus(toBeValidated, exam_id),
        enabled: isAllowed && !!exam_id,
    })
}


export const useChangeExamStatus = (exam_id:string) => {
    return useMutation({
        mutationFn: (data:{toBeValidated: string | null, exam_id: string, status: 'Active' | 'Draft'}) => changeExamStatus(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['status', exam_id] }),
    })
}


export function useExamChapterList(toBeValidated: string | null, exam_id: string | undefined, is_enabled: boolean | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['chapters', exam_id],
        queryFn: () => getExamChapters(toBeValidated, exam_id),
        enabled: isAllowed && !!exam_id && !!is_enabled
    })
}

export function useAssignChaptersByAIRag(toBeValidated: string | null, exam_id: string | undefined, grade_ids: number[], is_enabled: boolean | undefined) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['assign-chapters-ai', exam_id, grade_ids],
        queryFn: () => getAssignChaptersByAIRag(toBeValidated, exam_id, grade_ids),
        enabled: isAllowed && !!exam_id && !!is_enabled && grade_ids.length > 0
    })
}


export const useChangeAddQuestionChapter = (exam_id:string) => {
    return useMutation({
        mutationFn: (data:{toBeValidated: string | null, exam_id: string, data: {[code: number]: string}}) => changeAddQuestionChapter(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', exam_id] }),
    })
}


export const useSchoolChangeAddQuestion = (exam_id:string) => {
    return useMutation({
        mutationFn: (data:{toBeValidated: string | null, exam_id: string, data: {[code: number]: string}}) => schoolChangeAddQuestion(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', exam_id] }),
    })
}

export const useEditExamInfo = (exam_id: string) => {
    return useMutation({
        mutationFn: (data: { toBeValidated: string | null, exam_id: string, payload: { name?: string; time_minutes?: number | string; time?: number | string } }) => editExamInfo(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exams', exam_id] }),
    })
}

export function useExamList(toBeValidated: string | null, page_no: string | null, grade_id: string | null, sub_id: string | null, search?: string | null) {
    const { role } = useAuth();
    const isAllowed = !!toBeValidated && (role === 'superuser' || role === 'school' || role === 'college_university');
    return useQuery({
        queryKey: ['exams', page_no, grade_id, sub_id, search?.trim() || ''],
        queryFn: () => getExamsList(toBeValidated, page_no, grade_id, sub_id, search),
        placeholderData: keepPreviousData,
        enabled: isAllowed && !!page_no,
    })
}

export function useNationalExams(targetLevel?: 'school' | 'college_university', enabled: boolean = true) {
    return useQuery({
        queryKey: ['national_exams', targetLevel ?? 'all'],
        queryFn: () => getNationalExams(targetLevel),
        enabled,
    })
}

export function useUserInfo() {
    return useQuery({
        queryKey: ['user_info'],
        queryFn: () => getUserInfo(),
        enabled: true,
    })
}

export function useSharedNotes() {
    const { token } = useAuth();
    return useQuery({
        queryKey: ['shared_notes'],
        queryFn: () => getSharedNotes(token),
        enabled: !!token,
    })
}

export function useCreateSharedNote() {
    const { token } = useAuth();
    return useMutation({
        mutationFn: (data: { title: string; content: string }) => createSharedNote(token, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared_notes'] }),
    })
}

export function useUpdateSharedNote() {
    const { token } = useAuth();
    return useMutation({
        mutationFn: (data: { id: number; title: string; content: string }) => updateSharedNote(token, data.id, { title: data.title, content: data.content }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared_notes'] }),
    })
}

export function useAssignSharedNote(exam_id: string) {
    const { token } = useAuth();
    return useMutation({
        mutationFn: (data: { note_id: number; question_ids: number[] }) => assignSharedNoteToQuestions(token, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', exam_id] }),
    })
}

export function useDeleteExamQuestion(exam_id: string) {
    const { token } = useAuth();
    return useMutation({
        mutationFn: (data: { question_id: number | string }) => deleteExamQuestion(token, exam_id, data.question_id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', exam_id] }),
    })
}
